import type { TypedSupabaseClient } from '../client';
import type {
  IProSettingsRepository,
  ProSettings,
  UpdateProSettingsInput,
} from '../../../interfaces/src';

// Fallbacks matching the DB column defaults — used only if a tenant row somehow
// doesn't exist yet (the migration + tenant trigger seed one for every tenant,
// so this is belt-and-braces, never a silent 0 %).
const DEFAULTS: ProSettings = {
  defaultCommissionRate: 10,
  deliveryFee: 7,
  freeDeliveryThreshold: 0,
};

const COLS = 'default_commission_rate, delivery_fee, free_delivery_threshold';

export class SupabaseProSettingsRepository implements IProSettingsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getForCurrentTenant(): Promise<ProSettings> {
    const tenantId = await resolveTenantId(this.supabase);
    const { data, error } = await (this.supabase as any)
      .from('pro_settings')
      .select(COLS)
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSettings(data) : DEFAULTS;
  }

  async update(input: UpdateProSettingsInput): Promise<ProSettings> {
    const tenantId = await resolveTenantId(this.supabase);
    const patch: Record<string, unknown> = {
      tenant_id: tenantId,
      updated_at: new Date().toISOString(),
    };
    if (input.defaultCommissionRate !== undefined) {
      patch['default_commission_rate'] = clampPct(input.defaultCommissionRate);
    }
    if (input.deliveryFee !== undefined) {
      patch['delivery_fee'] = Math.max(0, Number(input.deliveryFee) || 0);
    }
    if (input.freeDeliveryThreshold !== undefined) {
      patch['free_delivery_threshold'] = Math.max(0, Number(input.freeDeliveryThreshold) || 0);
    }

    const { data, error } = await (this.supabase as any)
      .from('pro_settings')
      .upsert(patch, { onConflict: 'tenant_id' })
      .select(COLS)
      .single();
    if (error) throw error;
    return mapSettings(data as Record<string, unknown>);
  }
}

async function resolveTenantId(supabase: TypedSupabaseClient): Promise<string> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: string | null; error: { message: string } | null }>)
    .call(supabase, 'current_user_tenant_id', {});
  if (error) throw error;
  if (!data) throw new Error('current_tenant_not_found');
  return data;
}

function clampPct(n: number): number {
  return Math.min(100, Math.max(0, Number(n) || 0));
}

function mapSettings(row: Record<string, unknown>): ProSettings {
  return {
    defaultCommissionRate: Number(row['default_commission_rate'] ?? DEFAULTS.defaultCommissionRate),
    deliveryFee: Number(row['delivery_fee'] ?? DEFAULTS.deliveryFee),
    freeDeliveryThreshold: Number(row['free_delivery_threshold'] ?? DEFAULTS.freeDeliveryThreshold),
  };
}
